/** @jsx jsx */
import { isFunction, pick } from 'lodash';
import { css, jsx } from '@emotion/core';
import { Alert } from '@zapier/design-system';
import useFormState from '@zapier/payment/src/utils/useFormState';
import PaymentFormButtons from '@zapier/payment/src/components/PaymentFormButtons';
import {
  ApiKeys,
  CreditCardBrandCode,
  FieldName,
  FieldValue,
  HostedFieldAdditionalData,
  HostedFieldToken,
  PaymentDetailsCard,
  PaymentProcessor,
  PaymentSourceInput,
  PaymentSourceType,
} from '@zapier/payment/src/types';
import { AsyncOverlay } from '@zapier/payment/src/components/AsyncOverlay';
import CreditCardErrors from './CreditCardErrors';
import CreditCardFieldset from './CreditCardFieldset';
import { HostedFieldsProvider } from './HostedFields';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Payment processor to use */
  paymentProcessor: PaymentProcessor;
  /** Error message to show */
  error?: string | null;
  /** Show a cancel button */
  hasCancelButton?: boolean;
  /** User has saved payment source */
  hasPaymentSource: boolean;
  /** Editing is busy */
  isInFlight: boolean;
  /** Call to cancel current edit */
  onCancel: () => void;
  /** Called when card number changes */
  onCardNumberChange?: (brand?: CreditCardBrandCode | null) => void;
  /** Call when error happens during edit */
  onError: (error: Error) => void;
  /** Call to save current edit */
  onSave: (token: PaymentSourceInput) => void;
  /** Saved credit card details */
  savedCard: PaymentDetailsCard | undefined | null;
};

const formCss = css`
  display: grid;
  grid-gap: 10px;
`;

const CreditCardForm: React.FC<Props> = props => {
  const {
    state,
    updateErrors,
    checkIsValid,
    clearErrors,
    getAdditionalData,
    getFieldsErrors,
    startTokenization,
    stopTokenization,
    updateField,
  } = useFormState(PaymentSourceType.card, props.savedCard);

  const handleChange = (name: FieldName, value: FieldValue) => {
    // If changing card number state, trigger callback with card brand
    if (isFunction(props.onCardNumberChange) && name === 'number') {
      props.onCardNumberChange(typeof value === 'string' ? null : value.brand);
    }

    updateField(name, value);
  };

  const handleSave = (
    createToken: (
      additionalData: HostedFieldAdditionalData
    ) => Promise<HostedFieldToken>
  ) => async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = checkIsValid();

    if (isValid) {
      startTokenization();

      try {
        const { token, details } = await createToken(getAdditionalData());
        clearErrors();
        const inputDetails = pick(details, [
          'exp_month',
          'exp_year',
          'address_city',
          'address_state',
          'address_zip',
          'address_country',
        ]);
        // Depending on the payment processor, method may or may not be present
        // Hardcode it here so we can use it for tracking
        props.onSave({
          // @ts-ignore - these types are all sorts of confusing, this needs some work to get it working correctly
          details: inputDetails,
          token,
          method: PaymentSourceType.card,
          paymentProcessor: props.paymentProcessor,
        });
      } catch (error) {
        clearErrors();
        props.onError(error);
      } finally {
        stopTokenization();
      }
    } else {
      updateErrors();
    }
  };

  const isBusy = props.isInFlight || state.isTokenizing;
  const hasServerError = !state.isDirty && props.error;
  const hasFormError = state.hasErrors;
  const submitText = `${props.hasPaymentSource ? 'Update' : 'Add'} Card`;

  return (
    <HostedFieldsProvider
      apiKeys={props.apiKeys}
      onChange={handleChange}
      paymentProcessor={props.paymentProcessor}
    >
      {createToken => (
        <AsyncOverlay isVisible={isBusy}>
          <form
            css={formCss} // Used by E2E tests
            name="cc-form"
            onSubmit={handleSave(createToken)}
          >
            {hasServerError ? (
              <Alert type="error">{props.error}</Alert>
            ) : (
              hasFormError && <CreditCardErrors errors={getFieldsErrors()} />
            )}
            <CreditCardFieldset fields={state.fields} onChange={handleChange} />
            <PaymentFormButtons
              buttonText={submitText}
              canSubmit={state.isDirty}
              hasCancelButton={props.hasCancelButton}
              isBusy={isBusy}
              onCancel={() => {
                // Clear card type when credit card form is not shown
                if (props.onCardNumberChange) {
                  props.onCardNumberChange(null);
                }

                props.onCancel();
              }}
            />
          </form>
        </AsyncOverlay>
      )}
    </HostedFieldsProvider>
  );
};

export type CreditCardFormProps = Props;
export default CreditCardForm;
